import { getReadableDate } from "Library/dateHelper";
import {
  filterTimeSlots,
  generateMeetingTimes,
} from "Services/counselor-time-slot-picker-service";
import type {
  TSchedule,
  TScheduleItem,
} from "Services/types/counselor-grid-service-types";

// The advisors endpoint sends a day's slots either flat or split by meeting
// type, the same two shapes the counselor cards handle.
interface TAdvisorScheduleDay {
  date: string;
  isActive?: boolean;
  schedule?:
    | TScheduleItem[]
    | { all?: TScheduleItem[]; phone?: TScheduleItem[] };
}

// The counselor cards offer 45 minute meetings, so the footer promises a time
// the visitor can actually book once they land on the listing.
const MEETING_LENGTH = 45;
const DAYS_AHEAD = 5;

// A bookable meeting, kept as its sortable parts alongside the string the bar
// paints: the readable text is localized and relative ("idag 15:00"), so it is
// the wrong thing to compare two candidates on.
interface TBookable {
  date: string;
  startMinutes: number;
  text: string;
}

function normalizeDay(day: TAdvisorScheduleDay): TSchedule {
  const slots = Array.isArray(day.schedule)
    ? day.schedule
    : day.schedule?.phone || [];

  return { date: day.date, isActive: !!day.isActive, schedule: slots };
}

function toMinutes(time: string): number {
  const [hours, minutes] = time.split(":").map(Number);

  return hours * 60 + minutes;
}

// Dates arrive as YYYY-MM-DD, which sorts as text.
function isSooner(
  date: string,
  startMinutes: number,
  best: TBookable | null
): boolean {
  if (!best) {
    return true;
  }

  return date === best.date
    ? startMinutes < best.startMinutes
    : date < best.date;
}

interface TSchedulable {
  schedule?: unknown;
}

/**
 * The soonest bookable meeting across a list of advisors, as the same readable
 * string a single schedule gives. Every advisor's whole schedule is searched:
 * the endpoint returns them in its own order, which is not soonest-first, and
 * a day's slots are not sorted either.
 *
 * Shared so the bar and the cache it reads can never compute this differently.
 */
export function getFirstAvailableTimeFor(advisors: TSchedulable[]): string {
  let best: TBookable | null = null;

  for (const { schedule } of advisors) {
    const bookable = getFirstBookable(schedule as TAdvisorScheduleDay[], best);

    if (bookable) {
      best = bookable;
    }
  }

  return best ? best.text : "";
}

/**
 * The first bookable meeting in an advisor's schedule, as a readable date and
 * time — "today 15:00". Empty when nothing in the schedule can be booked.
 */
export function getFirstAvailableTime(
  schedule: TAdvisorScheduleDay[] | undefined
): string {
  const bookable = getFirstBookable(schedule, null);

  return bookable ? bookable.text : "";
}

/**
 * The soonest meeting in one schedule, or null when the schedule holds nothing
 * sooner than `best` — the caller's running winner, which lets a slot that
 * cannot win be skipped before its meetings are generated.
 */
function getFirstBookable(
  schedule: TAdvisorScheduleDay[] | undefined,
  best: TBookable | null
): TBookable | null {
  if (!Array.isArray(schedule)) {
    return null;
  }

  const days = schedule
    .filter(({ isActive }) => isActive)
    .map(normalizeDay)
    .reduce(
      (bookable: TSchedule[], day) =>
        filterTimeSlots(bookable, day, DAYS_AHEAD),
      []
    );

  let soonest: TBookable | null = null;

  for (const { date, schedule: slots } of days) {
    for (const slot of slots) {
      const startMinutes = toMinutes(slot.startTime);

      if (
        !(
          isSooner(date, startMinutes, soonest) &&
          isSooner(date, startMinutes, best)
        )
      ) {
        continue;
      }

      const [meeting] = generateMeetingTimes([slot], date, MEETING_LENGTH);

      if (!meeting) {
        continue;
      }

      // The generated start, so the bar reads the same as the counselor
      // cards above it: generateMeetingTimes formats the clock the
      // visitor's own browser uses, which is what both are meant to show.
      // The day it falls on comes from the site's locale instead, as it
      // does on the cards, abbreviated so the line fits the bar.
      //
      // Worth knowing before changing either: the booking form submits
      // this same generated string as start_date, so the format is part of
      // the payload and not only a display concern.
      soonest = {
        date,
        startMinutes,
        text: `${getReadableDate(date, false, true)} ${meeting.startTime}`,
      };
    }
  }

  return soonest;
}
